import { useCallback, useEffect, useState } from 'react';
import { Check, ChevronsUpDown, Loader2, Search } from 'lucide-react';
import { Button } from '../../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '../../ui/command';
import { cn } from '../../../lib/utils';
import { apiService, Customer } from '../../../services/api';

/** Wartezeit vor der Suchanfrage, damit Tippen keine Anfrage je Anschlag auslöst. */
const SEARCH_DEBOUNCE_MS = 300;

/** Kunden pro Trefferliste — reicht für eine Auswahl per Suche, ohne alles zu laden. */
const SEARCH_LIMIT = 20;

export interface SeverityCustomerPickerProps {
  selectedCustomer: Customer | null;
  onSelect: (customer: Customer) => void;
  /** Blockiert die Auswahl, solange ungespeicherte Änderungen im Puffer liegen. */
  disabled?: boolean;
}

/**
 * Kunden-Auswahl für den Kunden-Geltungsbereich — serverseitige Suche über
 * `getCustomers({ search })`, weil die Kundenliste zu groß für ein Select ist.
 */
export function SeverityCustomerPicker({
  selectedCustomer,
  onSelect,
  disabled = false,
}: SeverityCustomerPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);

  const loadCustomers = useCallback(async (term: string) => {
    try {
      setLoading(true);
      const response = await apiService.getCustomers({
        search: term.trim() || undefined,
        limit: SEARCH_LIMIT,
      });
      setCustomers(response.customers);
    } catch {
      // Fehlerfall bleibt still: die Liste zeigt dann „Kein Kunde gefunden".
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => void loadCustomers(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [open, search, loadCustomers]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-[280px] justify-between font-normal"
        >
          <span className="flex min-w-0 items-center gap-2">
            <Search className="h-4 w-4 flex-none text-gray-400" />
            <span className={cn('truncate', !selectedCustomer && 'text-gray-400')}>
              {selectedCustomer?.name ?? 'Kundenname oder ID …'}
            </span>
          </span>
          <ChevronsUpDown className="h-4 w-4 flex-none opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[320px] p-0">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Kunden suchen …" value={search} onValueChange={setSearch} />
          <CommandList>
            {loading && (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Kunden werden geladen …
              </div>
            )}
            {!loading && customers.length === 0 && <CommandEmpty>Kein Kunde gefunden.</CommandEmpty>}
            {!loading &&
              customers.map((customer) => (
                <CommandItem
                  key={customer.id}
                  value={customer.id}
                  onSelect={() => {
                    onSelect(customer);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      selectedCustomer?.id === customer.id ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span className="min-w-0 flex-1 truncate">{customer.name}</span>
                  {!customer.isActive && <span className="ml-2 text-xs text-gray-400">inaktiv</span>}
                </CommandItem>
              ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
